import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { MotionPathPlugin } from 'gsap/MotionPathPlugin';

gsap.registerPlugin(MotionPathPlugin);

// Physics constants
const GRAVITY = 600; // px/s²
const PARTICLE_DRAG = 0.95;
const FLICKER_MIN = 0.02; // seconds
const FLICKER_MAX = 0.05; // seconds
const THERMAL_LAG_PERCENT = 0.05; // 5% of path length

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  size: number;
  color: string;
}

interface WeldingRevealProps {
  onComplete?: () => void;
}

const WeldingReveal: React.FC<WeldingRevealProps> = ({ onComplete }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const electrodeRef = useRef<HTMLDivElement>(null);
  const mainPathRef = useRef<SVGPathElement>(null);
  const whiteHotPathRef = useRef<SVGPathElement>(null);
  const yellowPathRef = useRef<SVGPathElement>(null);
  const redPathRef = useRef<SVGPathElement>(null);

  const particlesRef = useRef<Particle[]>([]);
  const animationFrameRef = useRef<number | undefined>(undefined);
  const lastTimeRef = useRef<number>(0);
  const pathLengthRef = useRef<number>(0);

  // Audio refs
  const audioContextRef = useRef<AudioContext | null>(null);
  const pannerRef = useRef<PannerNode | null>(null);
  const gainRef = useRef<GainNode | null>(null);
  const sourceRef = useRef<AudioBufferSourceNode | null>(null);

  const [isAnimating, setIsAnimating] = useState(false);
  const [showFinalText, setShowFinalText] = useState(false);

  // Initialize audio
  useEffect(() => {
    const initAudio = async () => {
      try {
        const audioContext = new AudioContext();
        audioContextRef.current = audioContext;

        const panner = audioContext.createPanner();
        panner.panningModel = 'HRTF';
        panner.distanceModel = 'linear';
        panner.refDistance = 1;
        panner.maxDistance = 10000;
        panner.rolloffFactor = 1;
        panner.coneInnerAngle = 360;
        panner.coneOuterAngle = 0;
        panner.coneOuterGain = 0;
        panner.positionY.value = 0;
        panner.positionZ.value = 0;
        pannerRef.current = panner;

        const gain = audioContext.createGain();
        gain.gain.value = 0.3;
        gainRef.current = gain;

        panner.connect(gain);
        gain.connect(audioContext.destination);

        // Create synthetic welding sound
        const createWeldingSound = () => {
          const duration = 15;
          const sampleRate = audioContext.sampleRate;
          const buffer = audioContext.createBuffer(1, sampleRate * duration, sampleRate);
          const data = buffer.getChannelData(0);

          let lastOut = 0;
          for (let i = 0; i < data.length; i++) {
            const white = Math.random() * 2 - 1;
            const brown = (lastOut + (0.02 * white)) / 1.02;
            lastOut = brown;

            const crackle = Math.random() > 0.99 ? Math.random() * 0.5 : 0;
            const hum = Math.sin(2 * Math.PI * 60 * i / sampleRate) * 0.1;

            data[i] = (brown * 0.7 + crackle + hum) * 0.3;
          }

          return buffer;
        };

        const buffer = createWeldingSound();
        const source = audioContext.createBufferSource();
        source.buffer = buffer;
        source.loop = true;
        source.connect(panner);
        sourceRef.current = source;

      } catch (error) {
        console.error('Failed to initialize audio:', error);
      }
    };

    initAudio();

    return () => {
      if (sourceRef.current) {
        try {
          sourceRef.current.stop();
        } catch (e) {
          // Ignore
        }
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  // Particle system update loop
  useEffect(() => {
    if (!isAnimating) return;

    const updateParticles = (currentTime: number) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const deltaTime = lastTimeRef.current ? (currentTime - lastTimeRef.current) / 1000 : 0;
      lastTimeRef.current = currentTime;

      // Clear canvas for transparency
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Update and draw particles
      particlesRef.current = particlesRef.current.filter(particle => {
        particle.vy += GRAVITY * deltaTime;
        particle.vx *= PARTICLE_DRAG;
        particle.vy *= PARTICLE_DRAG;
        particle.x += particle.vx * deltaTime;
        particle.y += particle.vy * deltaTime;
        particle.life -= deltaTime;

        if (particle.life > 0) {
          ctx.save();
          ctx.globalAlpha = Math.min(1, particle.life);

          // Draw glow
          ctx.shadowBlur = 10;
          ctx.shadowColor = particle.color;

          ctx.fillStyle = particle.color;
          ctx.beginPath();
          ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
          return true;
        }
        return false;
      });

      animationFrameRef.current = requestAnimationFrame(updateParticles);
    };

    animationFrameRef.current = requestAnimationFrame(updateParticles);

    return () => {
      if (animationFrameRef.current !== undefined) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isAnimating]);

  // Spawn particles at electrode position
  const spawnParticles = (x: number, y: number) => {
    const count = 5;
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 200 + Math.random() * 400;

      particlesRef.current.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 100,
        life: 0.5 + Math.random() * 0.5,
        size: 1 + Math.random() * 2,
        color: ['#fff', '#ffee00', '#ffaa00', '#ff6600'][Math.floor(Math.random() * 4)]
      });
    }
  };

  // Plasma flicker effect
  const createFlickerTimeline = (target: HTMLElement) => {
    const tl = gsap.timeline({ repeat: -1 });

    for (let i = 0; i < 50; i++) {
      const duration = FLICKER_MIN + Math.random() * (FLICKER_MAX - FLICKER_MIN);
      const opacity = 0.6 + Math.random() * 0.4;
      tl.to(target, {
        opacity,
        duration,
        ease: 'none'
      });
    }

    return tl;
  };

  // Start the welding animation
  const startAnimation = () => {
    const mainPath = mainPathRef.current;
    const electrode = electrodeRef.current;
    const whiteHotPath = whiteHotPathRef.current;
    const yellowPath = yellowPathRef.current;
    const redPath = redPathRef.current;
    const canvas = canvasRef.current;
    const container = containerRef.current;
    const svg = svgRef.current;

    if (!mainPath || !electrode || !whiteHotPath || !yellowPath || !redPath || !canvas || !container || !svg) return;

    // Set canvas size to match container
    const rect = container.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    // SVG viewBox dimensions for coordinate transformation
    const viewBoxWidth = 1200;
    const viewBoxHeight = 400;

    // Calculate scale factors to transform SVG coords to canvas coords
    // Account for preserveAspectRatio="xMidYMid meet"
    const containerAspect = rect.width / rect.height;
    const viewBoxAspect = viewBoxWidth / viewBoxHeight;

    let scaleX: number, scaleY: number, offsetX: number, offsetY: number;

    if (containerAspect > viewBoxAspect) {
      // Container is wider - SVG is height-constrained
      scaleY = rect.height / viewBoxHeight;
      scaleX = scaleY;
      offsetX = (rect.width - viewBoxWidth * scaleX) / 2;
      offsetY = 0;
    } else {
      // Container is taller - SVG is width-constrained
      scaleX = rect.width / viewBoxWidth;
      scaleY = scaleX;
      offsetX = 0;
      offsetY = (rect.height - viewBoxHeight * scaleY) / 2;
    }

    // Get path length
    const pathLength = mainPath.getTotalLength();
    pathLengthRef.current = pathLength;

    // Initialize paths as hidden
    [mainPath, whiteHotPath, yellowPath, redPath].forEach(path => {
      path.style.strokeDasharray = `${pathLength}`;
      path.style.strokeDashoffset = `${pathLength}`;
    });

    setIsAnimating(true);

    // Start audio
    if (sourceRef.current && audioContextRef.current) {
      try {
        if (audioContextRef.current.state === 'suspended') {
          audioContextRef.current.resume();
        }
        sourceRef.current.start();
      } catch (e) {
        console.error('Failed to start audio:', e);
      }
    }

    // Create plasma flicker
    const flickerTl = createFlickerTimeline(electrode);

    // Main animation timeline
    const mainTl = gsap.timeline({
      onComplete: () => {
        flickerTl.kill();
        setIsAnimating(false);
        setShowFinalText(true);
        if (sourceRef.current) {
          try {
            sourceRef.current.stop();
          } catch (e) {
            // Ignore
          }
        }
        if (onComplete) onComplete();
      }
    });

    // Animate electrode along path
    mainTl.to(electrode, {
      duration: 10,
      ease: 'none',
      motionPath: {
        path: mainPath,
        align: mainPath,
        autoRotate: false,
        alignOrigin: [0.5, 0.5]
      },
      onUpdate: function() {
        const progress = this.progress();
        const point = mainPath.getPointAtLength(progress * pathLength);

        // Transform SVG coordinates to canvas coordinates
        const canvasX = point.x * scaleX + offsetX;
        const canvasY = point.y * scaleY + offsetY;

        // Spawn particles at transformed coordinates
        spawnParticles(canvasX, canvasY);

        // Update spatial audio
        if (pannerRef.current && rect.width > 0) {
          const normalizedX = (canvasX / rect.width) * 2 - 1;
          pannerRef.current.positionX.value = normalizedX * 5;
        }

        // Update path reveals with thermal effects
        const currentOffset = pathLength * (1 - progress);

        // Main path
        mainPath.style.strokeDashoffset = `${currentOffset}`;

        // White hot (5% lag)
        const whiteHotOffset = Math.max(currentOffset - (pathLength * THERMAL_LAG_PERCENT), 0);
        whiteHotPath.style.strokeDashoffset = `${whiteHotOffset}`;

        // Yellow (10% lag)
        const yellowOffset = Math.max(currentOffset - (pathLength * THERMAL_LAG_PERCENT * 2), 0);
        yellowPath.style.strokeDashoffset = `${yellowOffset}`;

        // Red (20% lag)
        const redOffset = Math.max(currentOffset - (pathLength * THERMAL_LAG_PERCENT * 4), 0);
        redPath.style.strokeDashoffset = `${redOffset}`;
      }
    });
  };

  useEffect(() => {
    const timer = setTimeout(startAnimation, 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div ref={containerRef} className="relative w-full h-full flex items-center justify-center bg-transparent">
      {/* Canvas for particles - transparent background */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{ zIndex: 10, background: 'transparent' }}
      />

      {/* SVG for weld path and text */}
      <svg
        ref={svgRef}
        viewBox="0 0 1200 400"
        className="w-full h-full"
        preserveAspectRatio="xMidYMid meet"
        style={{ zIndex: 5 }}
      >
        <defs>
          {/* Plasma glow filter */}
          <filter id="plasma-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="8" result="blur" />
            <feColorMatrix
              in="blur"
              type="matrix"
              values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 18 -7"
              result="glow"
            />
            <feBlend in="SourceGraphic" in2="glow" mode="screen" />
          </filter>

          {/* Arc glow filter */}
          <filter id="arc-glow" x="-200%" y="-200%" width="400%" height="400%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="15" result="blur1" />
            <feGaussianBlur in="SourceGraphic" stdDeviation="30" result="blur2" />
            <feMerge>
              <feMergeNode in="blur2" />
              <feMergeNode in="blur1" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* Gradient for text */}
          <linearGradient id="text-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" style={{ stopColor: '#ffffff', stopOpacity: 1 }} />
            <stop offset="50%" style={{ stopColor: '#fbbf24', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: '#ef4444', stopOpacity: 1 }} />
          </linearGradient>

          {/* A-grade aluminum weld bead gradient - bright shiny silver */}
          <linearGradient id="weld-bead-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" style={{ stopColor: '#ffffff', stopOpacity: 1 }} />
            <stop offset="15%" style={{ stopColor: '#f8f8f8', stopOpacity: 1 }} />
            <stop offset="35%" style={{ stopColor: '#e8e8e8', stopOpacity: 1 }} />
            <stop offset="50%" style={{ stopColor: '#d8d8d8', stopOpacity: 1 }} />
            <stop offset="65%" style={{ stopColor: '#e0e0e0', stopOpacity: 1 }} />
            <stop offset="85%" style={{ stopColor: '#f0f0f0', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: '#ffffff', stopOpacity: 1 }} />
          </linearGradient>

          {/* Aluminum metallic shine overlay */}
          <linearGradient id="aluminum-shine" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" style={{ stopColor: '#ffffff', stopOpacity: 0.4 }} />
            <stop offset="20%" style={{ stopColor: '#ffffff', stopOpacity: 0.1 }} />
            <stop offset="40%" style={{ stopColor: '#ffffff', stopOpacity: 0.3 }} />
            <stop offset="60%" style={{ stopColor: '#ffffff', stopOpacity: 0.1 }} />
            <stop offset="80%" style={{ stopColor: '#ffffff', stopOpacity: 0.3 }} />
            <stop offset="100%" style={{ stopColor: '#ffffff', stopOpacity: 0.4 }} />
          </linearGradient>

          {/* Stacked dimes pattern for A-grade weld - subtle on bright aluminum */}
          <pattern id="weld-ripples" patternUnits="userSpaceOnUse" width="10" height="40" patternTransform="rotate(0)">
            <ellipse cx="5" cy="20" rx="4" ry="16" fill="none" stroke="rgba(180,180,180,0.3)" strokeWidth="1.5" />
            <ellipse cx="5" cy="20" rx="2.5" ry="12" fill="none" stroke="rgba(200,200,200,0.2)" strokeWidth="0.8" />
          </pattern>

          {/* Weld edge highlight */}
          <filter id="weld-edge" x="-10%" y="-10%" width="120%" height="120%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="1" result="blur" />
            <feOffset in="blur" dx="0" dy="1" result="offsetBlur" />
            <feMerge>
              <feMergeNode in="offsetBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* Path for weld bead - straight line for A-grade clean weld */}
          <path
            id="weld-path"
            d="M 100 200 L 1100 200"
            fill="none"
          />
        </defs>

        {/* Red cooling layer - heat affected zone */}
        <path
          ref={redPathRef}
          d="M 100 200 L 1100 200"
          fill="none"
          stroke="#ff4444"
          strokeWidth="38"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter="url(#plasma-glow)"
          opacity="0.5"
        />

        {/* Yellow hot layer - just behind the arc */}
        <path
          ref={yellowPathRef}
          d="M 100 200 L 1100 200"
          fill="none"
          stroke="#ffaa00"
          strokeWidth="30"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter="url(#plasma-glow)"
          opacity="0.7"
        />

        {/* White hot layer - molten pool */}
        <path
          ref={whiteHotPathRef}
          d="M 100 200 L 1100 200"
          fill="none"
          stroke="#ffffff"
          strokeWidth="22"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter="url(#plasma-glow)"
        />

        {/* Main weld bead - A-grade bright aluminum appearance */}
        <path
          ref={mainPathRef}
          d="M 100 200 L 1100 200"
          fill="none"
          stroke="url(#weld-bead-gradient)"
          strokeWidth="28"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter="url(#weld-edge)"
        />

        {/* Aluminum shine overlay - metallic reflection */}
        <path
          d="M 100 200 L 1100 200"
          fill="none"
          stroke="url(#aluminum-shine)"
          strokeWidth="28"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            opacity: showFinalText ? 1 : 0,
            transition: 'opacity 0.8s ease-in-out'
          }}
        />

        {/* Stacked dimes overlay pattern - the signature of a good TIG weld */}
        <path
          d="M 100 200 L 1100 200"
          fill="none"
          stroke="url(#weld-ripples)"
          strokeWidth="28"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            opacity: showFinalText ? 1 : 0,
            transition: 'opacity 1s ease-in-out'
          }}
        />

        {/* Weld toe highlight (top edge reflection) - bright aluminum edge */}
        <path
          d="M 100 186 L 1100 186"
          fill="none"
          stroke="rgba(255,255,255,0.8)"
          strokeWidth="2.5"
          strokeLinecap="round"
          style={{
            opacity: showFinalText ? 1 : 0,
            transition: 'opacity 1s ease-in-out 0.5s'
          }}
        />

        {/* Weld toe shadow (bottom edge) - subtle definition */}
        <path
          d="M 100 214 L 1100 214"
          fill="none"
          stroke="rgba(150,150,150,0.4)"
          strokeWidth="2"
          strokeLinecap="round"
          style={{
            opacity: showFinalText ? 1 : 0,
            transition: 'opacity 1s ease-in-out 0.5s'
          }}
        />

        {/* Final text that appears after animation - "Danny Dime" */}
        <text
          x="600"
          y="160"
          fontFamily="'Bebas Neue', sans-serif"
          fontSize="90"
          fontWeight="400"
          fill="url(#text-gradient)"
          textAnchor="middle"
          letterSpacing="6"
          style={{
            textShadow: '0 0 30px rgba(255,255,255,0.9)',
            filter: 'drop-shadow(0 5px 15px rgba(0,0,0,0.8))',
            opacity: showFinalText ? 1 : 0,
            transition: 'opacity 1.5s ease-in-out'
          }}
        >
          DANNY DIME
        </text>

        {/* Final text that appears after animation - "AI Welding Coach" */}
        <text
          x="600"
          y="280"
          fontFamily="'Oswald', sans-serif"
          fontSize="32"
          fontWeight="500"
          fill="#22d3ee"
          textAnchor="middle"
          letterSpacing="12"
          style={{
            textShadow: '0 0 15px rgba(34,211,238,0.6)',
            filter: 'drop-shadow(0 3px 10px rgba(0,0,0,0.8))',
            opacity: showFinalText ? 1 : 0,
            transition: 'opacity 1.5s ease-in-out 0.5s'
          }}
        >
          AI WELDING COACH
        </text>
      </svg>

      {/* Electrode (lead point) */}
      <div
        ref={electrodeRef}
        className="absolute w-8 h-8 rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, #ffffff 0%, #22d3ee 40%, transparent 100%)',
          boxShadow: '0 0 40px #22d3ee, 0 0 80px #22d3ee, 0 0 120px rgba(34, 211, 238, 0.5)',
          filter: 'url(#arc-glow)',
          zIndex: 20
        }}
      />
    </div>
  );
};

export default WeldingReveal;
